import type { ReactNode } from "react";

import { useBrandIdentity } from "@/features/branding/BrandingProvider";
import { usePortalTheme, useResolvedTheme } from "@/features/theme/PortalThemeProvider";

/** Background this logo spot sits on. `auto` follows the active theme. */
export type LogoSurface = "light" | "dark" | "auto" | "nav";

interface BrandLogoProps {
  /** Sizing classes for the uploaded logo image (e.g. "h-8 w-auto"). */
  className?: string;
  dashboard?: boolean;
  /** Accessible name. Ignored when `decorative` is set. */
  alt?: string;
  /** True when a nearby element already carries the accessible name. */
  decorative?: boolean;
  /**
   * Background behind the logo. Spots painted on a fixed dark panel (marketing
   * header/footer) pass "dark"; the portal/admin nav band passes "nav" (black
   * unless the Light appearance is active); everything else follows the active
   * appearance.
   */
  surface?: LogoSurface;
  /** Built-in wordmark rendered when no logo is configured. */
  children: ReactNode;
}

/**
 * Renders the logo for the background it sits on — the network's logo on
 * white-labelled surfaces, otherwise the admin-uploaded PODGO logo — falling
 * back to the wordmark passed as children. Single swap point for every header,
 * footer and marketing logo spot.
 */
export function BrandLogo({
  className,
  dashboard = false,
  alt,
  decorative,
  surface = "auto",
  children,
}: BrandLogoProps) {
  const brand = useBrandIdentity();
  const theme = useResolvedTheme();
  const portalTheme = usePortalTheme();
  // The nav band is black in Mixed and Dark, and only goes light in Light.
  const navSurface = portalTheme?.choice === "light" ? "light" : "dark";
  const effective =
    surface === "auto" ? theme : surface === "nav" ? navSurface : surface;
  const logoUrl = effective === "dark" ? brand.logoDark : brand.logoLight;

  const mark = logoUrl ? (
    <img
      src={logoUrl}
      alt={decorative ? "" : (alt ?? brand.name)}
      aria-hidden={decorative || undefined}
      className={className}
      style={dashboard && brand.isWhiteLabel ? { height: brand.dashboardLogo.height, maxWidth: "100%", objectFit: "contain" } : undefined}
      // Uploaded logos have arbitrary aspect ratios and every spot sizes by
      // height with `w-auto`, so hard width/height attributes would distort
      // them. Height is pinned by the caller's class, so any reflow is
      // horizontal and inside the header only.
      decoding="async"
      fetchPriority="high"
    />
  ) : brand.logoPending ? (
    // Hold the spot invisibly until we know which logo to show, so the
    // built-in wordmark never flashes before the configured logo.
    <span aria-hidden className={`${className ?? ""} inline-block min-w-[6rem] invisible`} />
  ) : (
    <>{children}</>
  );

  // White-label credit sits directly under the network's logo, everywhere.
  if (!brand.isWhiteLabel || !brand.showPoweredBy) return mark;
  return (
    <span className="inline-flex flex-col items-start">
      {mark}
      <span className="mt-1 text-[10px] font-medium leading-none opacity-70">Powered by PODGO</span>
    </span>
  );
}
